import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  setUserFromStorage,
  clearError,
} from '../redux/slices/authSlice';
import * as authService from '../services/authService';

export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  // Initialize auth from localStorage
  const initAuth = useCallback(() => {
    dispatch(setUserFromStorage());
  }, [dispatch]);

  // Login
  const login = useCallback(
    async (email, password) => {
      dispatch(loginStart());
      try {
        const response = await authService.loginWithEmail(email, password);
        dispatch(
          loginSuccess({
            user: response.user,
            token: response.token,
          })
        );
        return response;
      } catch (error) {
        dispatch(loginFailure(error.response?.data?.message || 'Login failed'));
        throw error;
      }
    },
    [dispatch]
  );

  // Register
  const register = useCallback(
    async (firstName, lastName, email, password, phone, dateOfBirth, gender) => {
      dispatch(loginStart());
      try {
        const response = await authService.registerWithEmail(firstName, lastName, email, password, phone, dateOfBirth, gender);
        return response;
      } catch (error) {
        dispatch(loginFailure(error.response?.data?.message || 'Registration failed'));
        throw error;
      }
    },
    [dispatch]
  );

  // Verify OTP
  const verifyOTP = useCallback(
    async (email, otp) => {
      dispatch(loginStart());
      try {
        const response = await authService.verifyOTP(email, otp);
        dispatch(
          loginSuccess({
            user: response.user,
            token: response.token,
          })
        );
        return response;
      } catch (error) {
        dispatch(loginFailure(error.response?.data?.message || 'Verification failed'));
        throw error;
      }
    },
    [dispatch]
  );

  // Verify phone OTP
  const verifyPhone = useCallback(
    async (phone, otp) => {
      try {
        const response = await authService.verifyPhoneOTP(phone, otp);
        return response;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  // Send phone OTP
  const sendPhoneOTP = useCallback(async (phone) => {
    try {
      const response = await authService.sendPhoneOTP(phone);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  // Password reset
  const resetPassword = useCallback(
    async (email, otp, newPassword) => {
      try {
        const response = await authService.resetPasswordWithOTP(email, otp, newPassword);
        return response;
      } catch (error) {
        dispatch(loginFailure(error.response?.data?.message || 'Password reset failed'));
        throw error;
      }
    },
    [dispatch]
  );

  // Complete profile
  const completeProfile = useCallback(
    async (dateOfBirth, gender) => {
      dispatch(loginStart());
      try {
        const response = await authService.completeProfile(dateOfBirth, gender);
        dispatch(
          loginSuccess({
            user: response.user,
            token: auth.token,
          })
        );
        return response;
      } catch (error) {
        dispatch(loginFailure(error.response?.data?.message || 'Profile completion failed'));
        throw error;
      }
    },
    [dispatch, auth.token]
  );

  // Logout
  const logoutUser = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  // Clear error
  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    // State
    user: auth.user,
    token: auth.token,
    isAuthenticated: auth.isAuthenticated,
    loading: auth.loading,
    error: auth.error,
    
    // Methods
    initAuth,
    login,
    register,
    verifyOTP,
    verifyPhone,
    sendPhoneOTP,
    resetPassword,
    completeProfile,
    logout: logoutUser,
    clearError: clearAuthError,
  };
};

export default useAuth;
